import { useState } from "react";
import { useTranslation } from "next-i18next";
import PropTypes from "prop-types";
import { useComponentVisible } from "../../hooks";
import { FilterDropdown } from "./FilterDropdown";
import { SortDropdown } from "./SortDropdown";
import { MenuButton } from "./MenuButton";
import { useFilterSort } from "./hooks/useFilterSort";
import { SORT, PRICE_ASC, PRICE_DESC, DISCOUNT, FEATURED } from "./constants";
import Icon from "../DropDown/Icon";
import styles from "./styles/Filter.module.css";

const HIDE_FILTERS = {
  colour: false,
  size: false,
  type: false,
  colourTags: false,
  sort: false,
};

export function Filter({
  products,
  filterLabels,
  filteredValues,
  onChange,
  sortBy,
  setSortBy,
  activeFilters,
  searchView = false
}) {
  // Hook that alerts clicks outside of the passed ref
  const { ref } = useComponentVisible(() => setShowPanel(HIDE_FILTERS));
  const [showPanel, setShowPanel] = useState(HIDE_FILTERS);
  const { productList } = useFilterSort({
    products,
    filteredValues,
    sortBy,
  });
  const { t } = useTranslation("filter");

  const findDiscountItems = () => {
    if (productList && productList.length > 0) {
      const discountValues = productList.map((product) => product.sortBy.discount);
      const hasDiscount = (value) => value > 0;
      const listHasDiscountedItem = discountValues.some(hasDiscount);
      return listHasDiscountedItem;
    }
  };

  const listHasDiscount = findDiscountItems();
  let sortValues = [DISCOUNT, PRICE_ASC, PRICE_DESC];
  if (listHasDiscount) {
    sortValues = [...sortValues];
  }

  const toggleFilterPanel = (key) => {
    setShowPanel((prevState) => ({
      [prevState]: false,
      [key]: !showPanel[key],
    }));
  };

  const sortContainerStyles = `
    ${searchView ? styles.openContainer : styles.dropdownContainer}
  `;

  const Sort = () => {
    return (
      <div className={sortContainerStyles}>
        {!searchView && (
          <button className={styles.button} onClick={() => toggleFilterPanel(SORT)}>
            {`${t("sort-label")} ${t(sortBy)}`}
            <Icon icon={"drop-down-arrow"} viewBox="0 0 9 4">
              <path d={"M8.19629 0L4.19629 4L0.196289 0H8.19629Z"} fill="black" />
            </Icon>
          </button>
        )}
        <SortDropdown
          sortValues={sortValues}
          showPanel={searchView ? true : showPanel[SORT]}
          sortBy={sortBy}
          setSortBy={setSortBy}
          searchView={searchView}
        />
      </div>
    );
  };

  const Filters = () => {
    return (
      <>
        {activeFilters.map((key) => {
          return (
            <div className={sortContainerStyles} key={key}>
              {!searchView && (
                <MenuButton
                  filterName={key}
                  filterLabels={filterLabels[key]}
                  filteredValues={filteredValues[key]}
                  onClick={() => toggleFilterPanel(key)}
                  onChange={onChange}
                />
              )}
              <FilterDropdown
                filterName={key}
                filterLabels={filterLabels[key]}
                filteredValues={filteredValues[key]}
                onChange={onChange}
                showPanel={searchView ? true : showPanel[key]}
                searchView={searchView}
              />
            </div>
          );
        })}
      </>
    );
  };

  const containerStyles = `
    ${searchView ? styles.searchContainer : styles.container}
  `;

  return (
    <div className={containerStyles}>
      <div className={styles.innerContainer} ref={ref}>
        <div className={styles.filtersContainer}>
          <Sort />
          <Filters />
        </div>
      </div>
    </div>
  );
}

Filter.propTypes = {
  title: PropTypes.string,
  filterLabels: PropTypes.shape({
    colour: PropTypes.objectOf(PropTypes.string),
    colourTags: PropTypes.objectOf(PropTypes.string),
    size: PropTypes.objectOf(PropTypes.string),
    type: PropTypes.objectOf(PropTypes.string),
  }),
  filteredValues: PropTypes.shape({
    colour: PropTypes.array,
    size: PropTypes.array,
    type: PropTypes.array,
    colourTags: PropTypes.array,
  }),
  onChange: PropTypes.func,
  sortBy: PropTypes.string,
  setSortBy: PropTypes.func,
  collection: PropTypes.shape({
    collectionDescription: PropTypes.string,
    handle: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  activeFilters: PropTypes.arrayOf(PropTypes.string),
};
